import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { apiService } from "../utils/api";
import html2pdf from "html2pdf.js";
import { FaDownload } from "react-icons/fa";
import "./ComparisonResults.css";

function ComparisonResults() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeTaken, setTimeTaken] = useState("0:00");
  const [accuracy, setAccuracy] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [student, setStudent] = useState(null);
  const [rollNo, setRollNo] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const resultsContainerRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setStudent(user);

    // If we have comparison results from navigation state, use them
    // if (location.state?.comparisonResults) {
    //   setComparisonResults(location.state.comparisonResults);

    //   setAccuracy(location.state.comparisonResults.accuracy);
    //   setSpeed(location.state.comparisonResults.typingSpeed);
    //   setTimeTaken(location.state.comparisonResults.timeTaken);

    fetchDocument();
    // } else {
    // Otherwise fetch the document and calculate comparison
    fetchDocumentAndCompare();
    // }

    async function fetchDocument() {
      try {
        const response = await apiService.getDocument(id);
        const data = response.data;

        if (data.success) {
          setDocument(data.document);
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching document");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchDocumentAndCompare() {
      try {
        setLoading(true);
        const response = await apiService.getDocumentWithComparison(id);
        const data = response.data;

        if (data.success) {
          setDocument(data.document);

          if (data.comparisonResults) {
            setComparisonResults(data.comparisonResults);
            setAccuracy(data.comparisonResults.accuracy || data.comparisonResults.matchPercentage);
            setSpeed(data.comparisonResults.typingSpeed || 0);
            setTimeTaken(data.comparisonResults.timeTaken || "0:00");
            setRollNo(data.student.rollNo);
          } else {
            setError("No comparison results available for this document");
          }
        } else {
          setError(data.message || "Failed to load document");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching document and comparison");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  }, [id, location.state]);

  const renderFormattedText = () => {
    if (comparisonResults?.formattedText) {
      return <div dangerouslySetInnerHTML={{ __html: comparisonResults.formattedText }} />;
    } else if (comparisonResults?.wordByWordComparison) {
      // Render from word-by-word comparison if formattedText is not available
      return (
        <div className="formatted-text">
          {comparisonResults.wordByWordComparison.map((item, index) => {
            if (item.correct) {
              return (
                <span key={index} className="correct">
                  {item.student}{" "}
                </span>
              );
            } else if (item.student === null) {
              return (
                <span key={index} className="missing">
                  {item.original}{" "}
                </span>
              );
            } else if (item.original === null) {
              return (
                <span key={index} className="extra">
                  {item.student}{" "}
                </span>
              );
            } else {
              return (
                <span key={index}>
                  <span className="incorrect">{item.student} </span>
                  <span className="correction">({item.original}) </span>
                </span>
              );
            }
          })}
        </div>
      );
    } else {
      // Fallback to regular content
      return <div className="document-text">{document.content}</div>;
    }
  };

  const handleDownloadPdf = () => {
    if (!resultsContainerRef.current) return;

    setGeneratingPdf(true);

    const element = resultsContainerRef.current;
    const filename = `${document.title}_results_${new Date().toISOString().slice(0, 10)}.pdf`;

    // Create a clone of the element to modify for PDF without affecting the display
    const clonedElement = element.cloneNode(true);

    // Apply PDF-specific styling to the clone
    clonedElement.classList.add("generating-pdf");

    // Find the formatted content and adjust its styling
    const formattedContent = clonedElement.querySelector(".formatted-content");
    if (formattedContent) {
      formattedContent.style.minHeight = "auto";
      formattedContent.style.maxHeight = "none";
      formattedContent.style.height = "auto";
    }

    const opt = {
      margin: [10, 10, 10, 10],
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(clonedElement)
      .set(opt)
      .save()
      .then(() => {
        setGeneratingPdf(false);
      })
      .catch((err) => {
        console.error("Error generating PDF:", err);
        setGeneratingPdf(false);
      });
  };

  if (loading) return <p>Loading results...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!document) return <p>Document not found</p>;

  return (
    <div className="comparison-results-page">
      <div className="results-actions">
        <button onClick={() => navigate("/dashboard/student")} className="back-button">
          Back to Dashboard
        </button>
        <button onClick={handleDownloadPdf} className="download-pdf-button" disabled={generatingPdf}>
          <FaDownload /> {generatingPdf ? "Generating PDF..." : "Download as PDF"}
        </button>
      </div>

      <div className="comparison-results-container" ref={resultsContainerRef}>
        <div className="results-header">
          {/* <h2>{document.title} - Results</h2> */}
          <h2>NEW PITMAN STENO TYPING INSTITUTE PRAYAGRAJ</h2>
        </div>

        {comparisonResults && (
          <>
            <div className="practice-results-header">
              <h3>Practice Results: {comparisonResults.totalOriginalWords} words</h3>
            </div>

            <div className="student-info-container">
              <div className="student-info">
                <div className="info-row">
                  <span className="info-label">Student:</span>
                  <span className="info-value">{student.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Roll No:</span>
                  <span className="info-value">{rollNo}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Time taken:</span>
                  <span className="info-value">{timeTaken}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Accuracy:</span>
                  <span className="info-value">{accuracy}%</span>
                </div>
              </div>

              <div className="document-info">
                <div className="info-row">
                  <span className="info-label">Date:</span>
                  <span className="info-value">{document.createdAt}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Text:</span>
                  <span className="info-value">{comparisonResults.totalOriginalWords} words</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Speed:</span>
                  <span className="info-value">{speed} wpm</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Words:</span>
                  <span className="info-value">
                    {comparisonResults.totalStudentWords}/{comparisonResults.totalOriginalWords}
                  </span>
                </div>
              </div>
            </div>

            <div className="answer-with-corrections">
              <h3>Your Answer with Corrections:</h3>
              <div className="formatted-content">{renderFormattedText()}</div>
            </div>

            <div className="stats-container">
              <div className="stat-item">
                <span className="stat-label">Total Missing:</span>
                <span
                  className="stat-value"
                  style={{
                    color: comparisonResults.matchPercentage > 80 ? "#27ae60" : comparisonResults.matchPercentage > 50 ? "#f39c12" : "#e74c3c",
                  }}
                >
                  {comparisonResults.wordByWordComparison.filter((item) => item.student == null).length}
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Total Wrong:</span>
                <span className="stat-value">{comparisonResults.wordByWordComparison.filter((item) => item.correct === false).length}</span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Dot Error:</span>
                <span className="stat-value">{comparisonResults.wordByWordComparison.filter((item) => (item.original === "." || item.original?.endsWith(".")) && item.correct === false).length * 3}</span>
              </div>
            </div>

            {/* {comparisonResults.missingWords && comparisonResults.missingWords.length > 0 && (
              <div className="missing-section">
                <h4>Missing Words</h4>
                <div className="missing-items">
                  {comparisonResults.missingWords.map((word, index) => (
                    <span key={index} className="missing-word">
                      {word}
                    </span>
                  ))}
                  {comparisonResults.missingWords.length === 50 && <span className="more-indicator">... and more</span>}
                </div>
              </div>
            )} */}
          </>
        )}
      </div>
    </div>
  );
}

export default ComparisonResults;
